// SortableCategoryItem.tsx with updated code for handling product updates
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { useDrag, useDrop } from "react-dnd";
import {
  ICategoryProduct,
  CategoryDroppableProps,
  DragItem,
  ItemTypes,
  SortableCategoryItemProductProps,
} from "../../../../models/state/IStoreProduct";
import { SortableProductRow } from "./sortable-product-row";
import { useStore } from "../../../../contexts/store-provider";
import { observer } from "mobx-react";
import { formatMessage } from "../../../../translations/format-message";
import {
  getDefaultActiveKeysProduct,
  resetCategoriesProducts,
} from "../../../../helpers/category-helper";
import { AccordionSelectCallback } from "react-bootstrap/esm/AccordionContext";
import toast from "react-hot-toast";

// Recursive component that handles category and subcategory rendering
const SortableCategoryItem: React.FC<SortableCategoryItemProductProps> = ({
  category,
  index,
  parentId,
  moveCategory,
  moveItem,
  onDetailClick,
  setCategories,
  storeId,
  openAccordions,
  setOpenAccordions,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { productManagementStore, categoryManagementStore } = useStore();
  const {
    GetProductListByCatIdService,
    getProductData,
    addUpdateStoreProductOrderState,
    resetAddUpdateStoreProductOrderState,
    getProductStoreFilter,
    GetProductListBySearchService,
  } = productManagementStore;
  const { allCategoryForProductStore } = categoryManagementStore;
  const [productArray, setProductArray] = useState<any[]>([]);
  const [accordionEventKey, setAccordionEventKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Fetch products when this category's accordion is opened
  useEffect(() => {
    if (!getProductStoreFilter.IsFilterActive) {
      const eventKey = `${parentId || "root"}-${index}-${category.categoryId}`;
      if (openAccordions.includes(eventKey) && category.categoryId) {
        const fetchProductsForCategory = async () => {
          setIsLoading(true);
          try {
            await GetProductListByCatIdService(
              Number(category.categoryId),
              storeId,
              false
            );
            const products =
              getProductData.get(Number(category.categoryId)) || [];
            setProductArray(products);

            // Update the category with products in the parent state
            setCategories((prevCategories: ICategoryProduct[]) => {
              const updateCategoryWithProducts = (
                categories: ICategoryProduct[]
              ): ICategoryProduct[] => {
                return categories.map((cat) => {
                  if (cat.categoryId === category.categoryId) {
                    return { ...cat, products };
                  }
                  if (cat.subCategories.length > 0) {
                    return {
                      ...cat,
                      subCategories: updateCategoryWithProducts(
                        cat.subCategories
                      ),
                    };
                  }
                  return cat;
                });
              };
              return updateCategoryWithProducts([...prevCategories]);
            });
          } catch (error) {
            console.error(
              `Error fetching products for category ${category.categoryId}:`,
              error
            );
          } finally {
            setIsLoading(false);
          }
        };

        fetchProductsForCategory();
      }
    } else {
      try {
        // If there's a search query, use search service, otherwise get products for open accordions

        // Update the categories with correct product data
        setCategories((prevCategories: ICategoryProduct[]) => {
          // Updated type from any[] to ICategoryProduct[]
          // Helper function to process a category and its subcategories recursively

          // Process the search results and open relevant accordions
          // First, identify which categories have products
          const categoriesWithProducts = new Set<number>();
          if (getProductData && getProductData.size > 0) {
            getProductData.forEach((products, categoryId) => {
              if (products && products.length > 0) {
                categoriesWithProducts.add(categoryId);
              }
            });
          } else {
            return resetCategoriesProducts(prevCategories);
          }

          const categoryIndices = new Map<number, number>(); // categoryId -> index in categories array
          const parentChildMap = new Map<number, number[]>(); // parentId -> childIds

          const processCategoryHierarchy = (
            category: ICategoryProduct,
            index: number,
            parentId: number | null = null
          ) => {
            const categoryId = Number(category.categoryId);
            categoryIndices.set(categoryId, index);

            if (parentId !== null) {
              if (!parentChildMap.has(parentId)) {
                parentChildMap.set(parentId, []);
              }
              parentChildMap.get(parentId)!.push(categoryId);
            }

            if (category.subCategories && category.subCategories.length > 0) {
              category.subCategories.forEach((subCat, subIndex) => {
                processCategoryHierarchy(subCat, subIndex, categoryId);
              });
            }
          };

          // Process all categories to build the index and parent-child maps
          prevCategories.forEach((cat, index) =>
            processCategoryHierarchy(cat, index)
          );

          // Create set of prevCategories to open (including parents of prevCategories with products)
          const categoriesToOpen = new Set<number>();

          // Helper function to add a category and all its ancestors to the set
          const addCategoryWithAncestors = (categoryId: number) => {
            // First, add the category itself
            categoriesToOpen.add(categoryId);

            const parentEntries = Array.from(parentChildMap.keys());

            for (let i = 0; i < parentEntries.length; i++) {
              const parentId = parentEntries[i];
              const childIds = parentChildMap.get(parentId) || [];

              if (childIds.includes(categoryId)) {
                addCategoryWithAncestors(parentId);
                break;
              }
            }
          };

          // Add each category with products and its ancestors
          categoriesWithProducts.forEach((categoryId) => {
            addCategoryWithAncestors(categoryId);
          });

          // Now create the set of accordion keys to open
          const newOpenAccordions = new Set<string>();

          // For each root category
          prevCategories.forEach((cat, index) => {
            const categoryId = Number(cat.categoryId);

            // If this category or any descendant has products, open it
            if (categoriesToOpen.has(categoryId)) {
              const eventKey = `root-${index}-${categoryId}`;
              newOpenAccordions.add(eventKey);
            }
          });

          // Update the open accordions
          // setOpenAccordions(newOpenAccordions);

          // Create deep copy to avoid direct state mutation
          const updatedCategories = JSON.parse(JSON.stringify(prevCategories));

          // Helper function to update products recursively
          const updateCategoryProducts = (
            category: ICategoryProduct
          ): ICategoryProduct => {
            const categoryId = Number(category.categoryId);

            // If this category has products in the data, update it
            if (categoriesWithProducts.has(categoryId)) {
              category.products = getProductData.get(categoryId) || [];
            }

            // Process subcategories if any
            if (category.subCategories && category.subCategories.length > 0) {
              category.subCategories = category.subCategories.map(
                updateCategoryProducts
              );
            }

            return category;
          };
          // Update all categories
          const updatedCat = updatedCategories.map(updateCategoryProducts);
          return updatedCat;
        });
      } catch (error) {
        console.error("Error refreshing products:", error);
      } finally {
        // setIsRefreshing(false);
      }
    }
  }, [
    category.categoryId,
    GetProductListByCatIdService,
    getProductData,
    index,
    openAccordions,
    parentId,
    setCategories,
    storeId,
    getProductStoreFilter.IsFilterActive,
  ]);

  const currentEventKey = `${parentId || "root"}-${index}-${
    category.categoryId
  }`;
  const isAccordionOpen = openAccordions.includes(currentEventKey);

  // Handle product order update success
  useEffect(() => {
    if (addUpdateStoreProductOrderState.success) {
      const fetchProductsAfterReorder = async () => {
        if (!getProductStoreFilter.IsFilterActive && isAccordionOpen) {
          try {
            await GetProductListByCatIdService(
              Number(category.categoryId),
              storeId,
              false
            );
            const products =
              getProductData.get(Number(category.categoryId)) || [];
            setProductArray(products);

            // Update the main categories state
            setCategories((prevCategories: ICategoryProduct[]) => {
              const updateCategoryWithProducts = (
                categories: ICategoryProduct[]
              ): ICategoryProduct[] => {
                return categories.map((cat) => {
                  if (cat.categoryId === category.categoryId) {
                    return { ...cat, products };
                  }
                  if (cat.subCategories.length > 0) {
                    return {
                      ...cat,
                      subCategories: updateCategoryWithProducts(
                        cat.subCategories
                      ),
                    };
                  }
                  return cat;
                });
              };
              return updateCategoryWithProducts([...prevCategories]);
            });
          } catch (error) {
            console.error("Error fetching products after reorder:", error);
          }
        } else if (getProductStoreFilter.IsFilterActive) {
          try {
            // Clear previous search results immediately
            setCategories((prev: ICategoryProduct[]) =>
              resetCategoriesProducts(prev)
            );

            await GetProductListBySearchService(getProductStoreFilter.Search);

            // Force UI update with new product data
            setCategories((prev: ICategoryProduct[]) => {
              if (getProductData.size === 0) {
                // toast.success("no_data_found");
                setOpenAccordions([]);
                return [];
              }

              const updateCategories = (
                cats: ICategoryProduct[]
              ): ICategoryProduct[] =>
                cats.map((cat) => ({
                  ...cat,
                  products: getProductData.get(Number(cat.categoryId)) || [],
                  subCategories: updateCategories(cat.subCategories),
                }));

              const finalUpdatedCategories = updateCategories(
                allCategoryForProductStore
              );
              setOpenAccordions(
                getDefaultActiveKeysProduct(finalUpdatedCategories)
              );
              return finalUpdatedCategories;
            });
          } catch (error) {
            toast.error(formatMessage("search_failed"));
          }
        }
      };

      fetchProductsAfterReorder();
      resetAddUpdateStoreProductOrderState();
    }
  }, [
    addUpdateStoreProductOrderState.success,
    accordionEventKey,
    category.categoryId,
    GetProductListByCatIdService,
    getProductData,
    resetAddUpdateStoreProductOrderState,
    setCategories,
    storeId,
    openAccordions.length,
    getProductStoreFilter.IsFilterActive,
    openAccordions,
    parentId,
    index,
    getProductStoreFilter.Search,
    isAccordionOpen,
    GetProductListBySearchService,
    allCategoryForProductStore,
    setOpenAccordions,
  ]);

  // Update products when getProductData changes
  useEffect(() => {
    if (accordionEventKey && getProductData) {
      const products = getProductData.get(Number(category.categoryId)) || [];
      setProductArray(products);
    }
  }, [getProductData, category.categoryId, accordionEventKey]);

  // Set up drag
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CATEGORY,
    item: () => ({
      type: ItemTypes.CATEGORY,
      id: category.categoryId,
      index,
      parentId,
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  interface DropResult {
    handlerId: string | symbol | null;
  }

  // Set up drop
  const [{ handlerId }, drop] = useDrop<DragItem, void, DropResult>(
    {
      accept: ItemTypes.CATEGORY,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      // Use canDrop to show visual feedback
      canDrop(item: DragItem, monitor) {
        const dragParentId = item.parentId;
        const hoverParentId = parentId;
        // Only allow dropping within the same parent level
        return dragParentId === hoverParentId;
      },
      // Only execute on drop, not on hover
      
      drop(item: DragItem, monitor) {
        if (!ref.current) {
          return;
        }
        const dragParentId = item.parentId;
        const dragCategoryId = Number(item.id);
        const hoverCategoryId = Number(category.categoryId);
        const hoverParentId = parentId;
        // Only allow reordering within the same parent level
        if (dragParentId !== hoverParentId) {
          return;
        }

        const dragIndex = item.index;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

         // Time to actually perform the action
        moveCategory(
          parentId,
          dragIndex,
          hoverIndex,
          dragCategoryId,
          hoverCategoryId
        );

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        
      },
    },
    [moveCategory, parentId, index, category.categoryId]
  );

  drag(drop(ref));

  // Track accordion open/close state
  const handleAccordionClick: AccordionSelectCallback = (e) => {
    setOpenAccordions(
      Array.isArray(e)
        ? e.filter((key): key is string => key !== null && key !== undefined)
        : [e].filter((key): key is string => key !== null && key !== undefined)
    );
  };



  return (
    <Accordion
      className="mt-2"
      alwaysOpen
      defaultActiveKey={openAccordions}
      activeKey={openAccordions}
      onSelect={handleAccordionClick}
    >
      <div className="categories-container">
        <div ref={ref} data-handler-id={handlerId}>
          <Accordion.Item
            key={category.categoryId}
            eventKey={currentEventKey}
            aria-activedescendant={`${currentEventKey}`}
          >
            <Accordion.Header
            >
              <div className="d-flex align-items-center w-100">
                <span className="me-3">
                  <i className="fas fa-grip-vertical drag-handle">
                    <svg
                      id="fi_8379631"
                      height="12"
                      viewBox="0 0 24 24"
                      width="12"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        id="drag-vertical"
                        d="m10 19a2 2 0 1 1 -2-2 2 2 0 0 1 2 2zm-2-9a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm8 14a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2zm0-7a2 2 0 1 0 2 2 2 2 0 0 0 -2-2z"
                        fill="rgb(0,0,0)"
                      ></path>
                    </svg>
                  </i>
                </span>
                <span className="flex-grow-1 text-wrap">{category.name}</span>
                {isAccordionOpen && isLoading && (
                  <span
                    className="ms-2 spinner-border spinner-border-sm text-primary"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
              </div>
            </Accordion.Header>
            <Accordion.Body>
              {/* Render products if available */}
              {category.products && category.products.length > 0 ? (
                <div className="table-responsive">
                  <table className="table border">
                    <thead>
                      <tr>
                        <th></th>
                        <th className="col-sr-no">{formatMessage("sr_no")}</th>
                        <th className="col-name">{formatMessage("name")}</th>
                        <th className="col-fee">{formatMessage("fee")}</th>
                        <th className="col-details">
                          {formatMessage("details")}
                        </th>
                      </tr>
                    </thead>
                    <CategoryDroppable id={category.categoryId}>
                      {category.products.map((product, index) => (
                        <SortableProductRow
                          key={`product-${product.Id}-${category.categoryId}`}
                          id={`product-${product.Id}-${category.categoryId}`}
                          product={product}
                          index={index}
                          categoryId={category.categoryId}
                          moveItem={moveItem}
                          onDetailClick={onDetailClick}
                        />
                      ))}
                    </CategoryDroppable>
                  </table>
                </div>
              ) : (category.products && category.products.length === 0 && category?.subCategories?.length === 0) ? (
                <div>{formatMessage("no_product_found_in_this_category")}</div>
              ) : null}

              {/* Recursively render subcategories */}
              {category.subCategories.length > 0 && (
                <>
                  {category.subCategories.map((subCategory, subIndex) => (
                    <SortableCategoryItem
                      key={subCategory.categoryId}
                      category={subCategory}
                      index={subIndex}
                      parentId={category.categoryId}
                      moveCategory={moveCategory}
                      moveItem={moveItem}
                      onDetailClick={onDetailClick}
                      setCategories={setCategories}
                      storeId={storeId}
                      openAccordions={openAccordions}
                      setOpenAccordions={setOpenAccordions}
                    />
                  ))}
                </>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </div>
      </div>
    </Accordion>
  );
};

export default observer(SortableCategoryItem);

// Droppable category component for products
export const CategoryDroppable: React.FC<CategoryDroppableProps> = observer(
  ({ id, children }) => {
    return <tbody>{children}</tbody>;
  }
);
